/* =========================================================
   Concern quiz results

   The quiz used to hand the shopper off to /search once they had answered
   everything, which threw away the card they were in and the answers with
   it. This fetches the same search as a fragment
   (templates/search.quiz.liquid) and renders it where they are.

   Shared by both places the quiz runs: the inline card in
   sections/sort-a-concern.liquid and the modal in assets/quiz.js. They ask
   the same four questions, so they show the same answer.

   Nothing here ever leaves the shopper with an empty card. A budget that
   matches nothing falls back to the full set of matches; a search that
   matches nothing, or a request that fails, falls back to a link to the
   search page — the behaviour we used to force on everyone.
   ========================================================= */
(function () {
  'use strict';

  if (window.VroQuizResults) return;

  var SHOW = 8;
  /* What a quiz result shows before the tile closes it. Five, because the
     phone lays these out three to a row (see custom.css) and five plus the
     tile is exactly two full rows. custom.css hides past the fifth at the
     same number — the two have to agree or the tile stops meaning "there
     are more". */
  var GRID_SHOW = 5;

  /* The budget chips, in the shop's smallest currency unit, matching the
     number Liquid stamps on each card. Keep these in step with BUDGETS in
     sections/sort-a-concern.liquid and snippets/quiz-modal.liquid. */
  var BUDGETS = {
    'Below ₹500': [0, 50000],
    '₹500 - ₹1000': [50000, 100000],
    '₹1000 - ₹1500': [100000, 150000],
    '₹1500 - ₹2000': [150000, 200000],
    /* Open-ended at the top. A number rather than Infinity: this pair is
       compared against the paise Liquid stamps on each card, and a real
       bound keeps that comparison the same shape as every other row. */
    'Above ₹2000': [200000, 100000000],

    /* The four bands the quizzes offer now, in the sheet's words. The five
       above stay readable: a profile saved before the change still has one
       of those words in it, and a band this map cannot read is a price
       gate silently skipped rather than a band that matches nothing. */
    'Under ₹500': [0, 50000],
    '₹500 - ₹1,000': [50000, 100000],
    '₹1,000 - ₹1,500': [100000, 150000],
    'Above ₹1,500': [150000, 100000000],

    /* Fashion's ladder, and fragrance's — a shirt and a shampoo are not
       priced alike, so the two departments are asked in their own bands. */
    'Under ₹1,000': [0, 100000],
    '₹1,000 - ₹2,500': [100000, 250000],
    '₹2,500 - ₹5,000': [250000, 500000],
    '₹5,000 - ₹10,000': [500000, 1000000],
    'Above ₹10,000': [1000000, 100000000],
    '₹1,000 - ₹2,000': [100000, 200000],
    '₹2,000 - ₹3,500': [200000, 350000],
    'Above ₹3,500': [350000, 100000000]
  };

  /* What the shopper told us, as something to search for. The category is
     cut at "&" — "Hair & Scalp" searches better as "Hair". */
  function terms(answers) {
    var out = [];
    if (answers.category) out.push(answers.category.split(' & ')[0]);
    if (answers.problems && answers.problems.length) out = out.concat(answers.problems);
    if (answers.condition) out.push(answers.condition);
    return out;
  }

  /* type=product is what makes the results page a product listing: without
     it Shopify also searches pages and articles, and the product filters —
     Brand, Size, Colour, Price — are not returned at all, which is why the
     page a quiz landed on had a filter bar holding nothing but Sort.

     vro_cat is the department the answers were about, so the line under
     the search bar can read "86 styles · Fashion & Lifestyle" rather than
     repeating the query back as a headline. */
  function searchUrl(answers) {
    var url = '/search?q=' + encodeURIComponent(terms(answers).join(' ')) + '&type=product';
    if (answers && answers.category) {
      url += '&vro_cat=' + encodeURIComponent(answers.category);
    }
    return url;
  }

  function fragmentUrl(answers) {
    return searchUrl(answers) + '&view=quiz';
  }

  /* A sentence for the shopper: what we searched for, in their words. */
  function summary(answers) {
    var parts = [];
    if (answers.problems && answers.problems.length) parts.push(answers.problems.join(', ').toLowerCase());
    if (answers.condition) parts.push(answers.condition.toLowerCase());
    if (answers.budget) parts.push(answers.budget);
    return parts.length ? 'For ' + parts.join(' · ') : '';
  }

  function withinBudget(card, range) {
    var price = parseInt(card.getAttribute('data-vro-price'), 10);
    if (!isFinite(price)) return true;
    return price >= range[0] && price <= range[1];
  }

  /* Narrow to the budget, but never to nothing: a shopper who said "below
     ₹500" in a catalogue that starts at ₹800 is better served by the
     matches we do have than by an empty shelf. */
  function applyBudget(cards, budget) {
    var range = BUDGETS[budget];
    if (!range) return { cards: cards, kept: true };
    var within = cards.filter(function (card) { return withinBudget(card, range); });
    if (!within.length) return { cards: cards, kept: false };
    return { cards: within, kept: true };
  }

  /* The two actions. `options.actionsInto`, when given, is where they go —
     the concern card puts them above its heading rather than under the
     products. Buttons either way: they are the two things to do with a
     result, and a text link reads as a footnote next to them. */
  function actions(answers, options, allLabel) {
    var wrap = el('div', 'quiz-results-actions');

    var all = el('a', 'quiz-results-btn quiz-results-btn--primary', allLabel);
    all.href = searchUrl(answers);
    wrap.appendChild(all);

    if (options.onRestart) {
      var again = el('button', 'quiz-results-btn quiz-results-btn--ghost', 'Retake');
      /* The same circular arrow every other Retake in the theme carries. */
      again.insertAdjacentHTML('afterbegin',
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>');
      again.type = 'button';
      again.addEventListener('click', options.onRestart);
      wrap.appendChild(again);
    }
    return wrap;
  }

  /* Empties a caller-supplied actions slot and shows it, or hands the
     actions back to be placed inline. */
  function placeActions(node, options) {
    var slot = options.actionsInto;
    if (!slot) return null;
    slot.innerHTML = '';
    slot.appendChild(node);
    slot.hidden = false;
    return slot;
  }

  /* The tile that closes a rail — the same one every feed-rail on the home
     page ends with, so a shopper who has scrolled to the end of their picks
     finds the way to the rest exactly where they find it everywhere else.
     Goes to the same place the "See all matches" button does. */
  function viewAllCard(answers) {
    return viewAllTile(searchUrl(answers), answers.category || 'matches');
  }

  /* The same tile, for a caller that knows its own destination — the
     ingredient searches below go to the search page for their term rather
     than to the four-answer one. */
  function viewAllTile(href, label) {
    var a = el('a', 'feed-view-all');
    a.href = href;

    var icon = el('span', 'feed-view-all-icon');
    icon.innerHTML =
      '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M9 3.5c3 0 5.5 1.8 6.5 4.3l4-1.3-1.8 3.9c1 .9 1.6 2 1.6 3.3 0 3.3-4.6 6-9.6 6-5.2 0-9.7-3-9.7-6.7C0 9.2 3.9 3.5 9 3.5Z"/>' +
      '<circle cx="6.5" cy="12.5" r="1.2" fill="currentColor" stroke="none"/></svg>';
    a.appendChild(icon);

    var text = el('span', 'feed-view-all-text');
    text.appendChild(el('span', 'feed-view-all-label', 'View All'));

    /* textContent, not innerHTML: the label is the shopper's own answer. */
    var cat = el('span', 'feed-view-all-category', label || 'matches');
    cat.insertAdjacentHTML('beforeend',
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>');
    text.appendChild(cat);

    a.appendChild(text);
    return a;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function emptyState(container, answers, message, options) {
    container.innerHTML = '';
    var wrap = el('div', 'quiz-results quiz-results--empty');
    wrap.appendChild(el('p', 'quiz-results-note', message));

    options = options || {};
    var node = actions(answers, options, 'Search the whole store');
    if (!placeActions(node, options)) wrap.appendChild(node);
    container.appendChild(wrap);
    if (options.onRendered) options.onRendered();
  }

  /* Renders into `container`. `options.onRestart`, when given, adds a
     "Retake" button wired to it; `options.actionsInto` puts both
     buttons in that element instead of under the products;
     `options.onRendered` runs once everything is in place — the actions
     slot is emptied when it is filled, so anything a caller wants to sit
     alongside the buttons has to be added after that, not before. */
  function render(container, answers, options) {
    options = options || {};
    container.innerHTML = '';
    container.appendChild(el('p', 'quiz-results-loading', 'Finding your picks…'));

    fetch(fragmentUrl(answers), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('search fragment failed');
        return res.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var cards = Array.prototype.slice.call(holder.querySelectorAll('.feed-card'));
        if (!cards.length) {
          emptyState(container, answers, 'Nothing matched all of that. Try fewer problems, or look through everything.', options);
          return;
        }

        var chosen = applyBudget(cards, answers.budget);

        container.innerHTML = '';
        var wrap = el('div', 'quiz-results');

        var note = summary(answers);
        if (note) wrap.appendChild(el('p', 'quiz-results-note', note));
        if (!chosen.kept) {
          wrap.appendChild(el('p', 'quiz-results-note quiz-results-note--soft',
            'Nothing in that budget, so here is everything that matched.'));
        }

        var rail = el('div', 'quiz-results-cards rail-scroll');
        chosen.cards.slice(0, SHOW).forEach(function (card) { rail.appendChild(card); });
        rail.appendChild(viewAllCard(answers));
        wrap.appendChild(rail);

        var node = actions(answers, options, 'See all matches');
        if (!placeActions(node, options)) wrap.appendChild(node);

        container.appendChild(wrap);
        if (options.onRendered) options.onRendered();
      })
      .catch(function () {
        emptyState(container, answers, "We couldn't load your picks just now.", options);
      });
  }

  /* ---------------------------------------------------------------
     One option, one search.

     The nutrition quiz asks which ingredients a shopper takes and then
     shows the supplements that contain them. Nothing in the catalogue is
     tagged by ingredient, so the ingredient is a search term — but every
     product in the category carries "Health & Nutrition" as a tag, so
     scoping the search to that tag is what keeps a search for "calcium"
     to supplements rather than anything else that mentions it.

     If the scoped search finds nothing the same search runs unscoped
     rather than showing an empty shelf: a shopper who asked about
     melatonin is better served by whatever mentions melatonin than by
     nothing at all.
     --------------------------------------------------------------- */
  function searchQueryUrl(query, view) {
    return '/search?q=' + encodeURIComponent(query) + (view ? '&type=product&view=quiz' : '');
  }

  function fetchCards(query) {
    return fetch(searchQueryUrl(query, true), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('search fragment failed');
        return res.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        return Array.prototype.slice.call(holder.querySelectorAll('.feed-card'));
      });
  }

  /* Is this card in the category the row is kept to?

     The category is a product tag, and every category collection in this
     shop is a smart collection on exactly that tag — "Health & Nutrition"
     the collection and tag:"Health & Nutrition" are the same 236 products —
     so the tag is a faithful stand-in for the collection.

     It is applied to the cards rather than to the query. The online store's
     search takes free text, not field filters: `potassium tag:"Health &
     Nutrition"` is searched for as those words and finds nothing, which
     used to drop the row back to an unscoped search and fill it with
     whatever the store had. The cards carry their own tags
     (templates/search.quiz.liquid renders them with tags_attr), so matching
     here is exact and needs nothing configured in the admin. */
  function inScope(card, scopeTag) {
    if (!scopeTag) return true;
    var tags = (card.getAttribute('data-vro-tags') || '').split('|');
    for (var i = 0; i < tags.length; i++) {
      if (tags[i].trim() === scopeTag) return true;
    }
    return false;
  }

  /* opts: { term, scopeTag, show, seeAllLabel }. Renders a rail into
     `container`. With a scopeTag the row is that category or nothing:
     showing a shopper who asked for potassium supplements a shampoo that
     happens to say the word is worse than telling them we have none. */
  function renderSearch(container, opts) {
    opts = opts || {};
    var term = (opts.term || '').trim();
    if (!term) return;
    var scopeName = (opts.scopeTag || '').trim();
    var scopeTag = scopeName.toLowerCase();
    var show = parseInt(opts.show, 10);
    if (!isFinite(show) || show < 1) show = 4;
    /* The phone draws this row as a 3-up grid, which wants five and then
       the tile. A section asking for more than five still gets it. */
    if (show < GRID_SHOW) show = GRID_SHOW;

    container.innerHTML = '';
    container.appendChild(el('p', 'quiz-results-loading', 'Finding your picks…'));

    fetchCards(term)
      .then(function (cards) {
        var scoped = cards.filter(function (card) { return inScope(card, scopeTag); });

        container.innerHTML = '';
        if (!scoped.length) {
          container.appendChild(el('p', 'quiz-results-note quiz-results-note--soft',
            scopeName
              ? 'Nothing in ' + scopeName + ' with ' + term + ' just now.'
              : 'Nothing with ' + term + ' just now.'));
          return;
        }
        var rail = el('div', 'quiz-results-cards rail-scroll');
        scoped.slice(0, show).forEach(function (card) { rail.appendChild(card); });

        /* Only worth offering the rest when there is a rest. Past the fifth
           the row closes on the tile, which goes to the search page holding
           the whole result — the same place the text link went, now the
           sixth cell of the grid rather than a line under it. */
        if (scoped.length > show) {
          rail.appendChild(viewAllTile(searchQueryUrl(term, false), scopeName || term));
        }
        container.appendChild(rail);
      })
      .catch(function () {
        container.innerHTML = '';
        container.appendChild(el('p', 'quiz-results-note quiz-results-note--soft',
          "We couldn't load those just now."));
      });
  }

  window.VroQuizResults = {
    render: render,
    renderSearch: renderSearch,
    searchUrl: searchUrl,
    summary: summary,
    /* Exported so a caller that renders its picks through another engine
       can still finish with the same two buttons this one ends on, rather
       than growing a second pair that drift apart. */
    actions: actions,
    /* The row length the phone's grid is built around, so the other engine
       counts to the same five. */
    gridShow: GRID_SHOW,
    viewAllTile: viewAllTile,
    /* Same reasoning for the tile that closes the row: the other engine
       (assets/vro-personalization.js) draws the same picks and should end
       on the same tile rather than build a second one. */
    viewAllCard: viewAllCard
  };
})();
